import { authClient } from "@/shared/lib/axios";
import {
	type CompleteUploadResponse,
	CompleteUploadResponseSchema,
	type DeleteUploadResponse,
	DeleteUploadResponseSchema,
	type DownloadUrlResponse,
	DownloadUrlResponseSchema,
	type ListWorkflowUploadsResponse,
	ListWorkflowUploadsResponseSchema,
	type PresignUploadRequest,
	type PresignUploadResponse,
	PresignUploadRequestSchema,
	PresignUploadResponseSchema,
} from "./workflow-upload.types";

const endpoint = (workflowId: string) => `/workflows/${workflowId}/uploads`;

export const workflowUploadApi = {
	list: async (workflowId: string): Promise<ListWorkflowUploadsResponse> => {
		const { data } = await authClient.get(endpoint(workflowId));
		return ListWorkflowUploadsResponseSchema.parse(data);
	},

	presignUpload: async (
		workflowId: string,
		request: PresignUploadRequest,
	): Promise<PresignUploadResponse> => {
		const payload = PresignUploadRequestSchema.parse(request);
		const { data } = await authClient.post(`${endpoint(workflowId)}/presign-upload`, payload);
		return PresignUploadResponseSchema.parse(data);
	},

	completeUpload: async (
		workflowId: string,
		uploadId: string,
		size?: number,
	): Promise<CompleteUploadResponse> => {
		const { data } = await authClient.post(
			`${endpoint(workflowId)}/${uploadId}/complete`,
			size !== undefined ? { size } : {},
		);
		return CompleteUploadResponseSchema.parse(data);
	},

	delete: async (workflowId: string, uploadId: string): Promise<DeleteUploadResponse> => {
		const { data } = await authClient.delete(`${endpoint(workflowId)}/${uploadId}`);
		return DeleteUploadResponseSchema.parse(data);
	},

	getDownloadUrl: async (
		workflowId: string,
		uploadId: string,
	): Promise<DownloadUrlResponse> => {
		const { data } = await authClient.get(
			`${endpoint(workflowId)}/${uploadId}/download-url`,
		);
		return DownloadUrlResponseSchema.parse(data);
	},

	uploadFile: async (uploadUrl: string, file: File, contentType: string): Promise<void> => {
		const response = await fetch(uploadUrl, {
			method: "PUT",
			headers: { "Content-Type": contentType },
			body: file,
		});
		if (!response.ok) {
			throw new Error(`Upload failed: ${response.status}`);
		}
	},
};
